// frontend/src/pages/Search.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { search as searchApi, importDatabaseCustomers, importFinalUpdated } from "../api.js";

import SearchBar from "../components/SearchBar.jsx";
import ResultsTable from "../components/ResultsTable.jsx";
import QuickFilterChips from "../components/QuickFilterChips.jsx";
import ActiveFiltersDisplay from "../components/ActiveFiltersDisplay.jsx";
import AdvancedFilterModal from "../components/AdvancedFilterModal.jsx";
import { useNotify } from "../components/Notify.jsx";

function toInt(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function arrify(v) {
  if (v === undefined || v === null) return [];
  return Array.isArray(v) ? v : [v];
}

// Keys that can appear multiple times in URL (and should be stored as arrays)
const ARRAY_KEYS = new Set([
  "status",
  "risk_level",
  "residency_status",
  "legal_form",
  "country_id",
  "review_status",
  "coc_status",
  "screening_status",
  "license_status",
  "services",
  "transaction_types",
]);

// Keys that are "single select but we store as scalar" (kept as scalar in state)
const SCALAR_KEYS = new Set([
  "q",
  "country",
  "page",
  "pageSize",
  "created_within",
  "updated_within",
  "managed_by",
  "business_activity",
  "bank_account",
  "has_documents",
  "started_from",
  "started_to",
]);

function parseFromUrl(searchParams) {
  const tab = searchParams.get("tab") || "companies";

  const out = {
    // UI state
    tab,

    // scalar search params
    q: searchParams.get("q") || "",
    country: searchParams.get("country") || "",
    page: toInt(searchParams.get("page"), 1),
    pageSize: toInt(searchParams.get("pageSize"), 20),
  };

  // arrays
  ARRAY_KEYS.forEach((k) => {
    const vals = searchParams.getAll(k).filter(Boolean);
    if (vals.length) out[k] = vals;
  });

  // scalars
  SCALAR_KEYS.forEach((k) => {
    if (k === "q" || k === "country" || k === "page" || k === "pageSize") return;
    const v = searchParams.get(k);
    if (v !== null && v !== undefined && v !== "") out[k] = v;
  });

  return out;
}

function buildUrlParams(tab, params) {
  const url = new URLSearchParams();

  url.set("tab", tab);

  // Always keep page/pageSize stable in URL (page defaults to 1)
  const page = toInt(params.page, 1);
  const pageSize = toInt(params.pageSize, 20);
  url.set("page", String(page));
  url.set("pageSize", String(pageSize));

  // Scalars
  Object.entries(params).forEach(([k, v]) => {
    if (k === "tab") return;
    if (k === "type") return; // derived from tab
    if (k === "page" || k === "pageSize") return;

    if (v === undefined || v === null || v === "") return;

    if (Array.isArray(v)) return; // handled below
    url.set(k, String(v));
  });

  // Arrays
  Object.entries(params).forEach(([k, v]) => {
    if (!Array.isArray(v) || v.length === 0) return;
    v.filter(Boolean).forEach((item) => url.append(k, String(item)));
  });

  return url;
}

// Normalize quick-filters / legacy keys into the backend-friendly keys.
// (This keeps things "prod-safe": you can change the chips without breaking the API.)
function normalizeIncomingFilters(incoming = {}) {
  const next = { ...incoming };

  // Quick filter chips in one version used { review_overdue: true } etc.
  if (next.review_overdue) {
    delete next.review_overdue;
    next.review_status = ["overdue"];
  }
  if (next.coc_overdue) {
    delete next.coc_overdue;
    next.coc_status = ["overdue"];
  }
  if (next.has_screening_hits) {
    delete next.has_screening_hits;
    next.screening_status = ["hits"];
  }

  // Risk level: support "very high" vs "very_high"
  if (Array.isArray(next.risk_level)) {
    next.risk_level = next.risk_level.map((r) => (r === "very high" ? "very_high" : r));
  } else if (typeof next.risk_level === "string") {
    next.risk_level = next.risk_level === "very high" ? "very_high" : next.risk_level;
  }

  // created_within can be passed as ["30"] or "30"
  if (Array.isArray(next.created_within)) {
    next.created_within = next.created_within[0] || "";
  }

  // residency_status can be array or string; backend accepts array via repeated params
  // Keep as array if already array, otherwise scalar is fine.
  return next;
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const notify = useNotify();

  // UI state
  const tab = searchParams.get("tab") || "companies";

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Import/enrich state (file-based; aligns with your current api.js functions)
  const [importing, setImporting] = useState(false);
  const [enriching, setEnriching] = useState(false);

  // Filters state (scalar + arrays)
  const [filters, setFilters] = useState({});

  // Avoid double initial fetch loops
  const didInitRef = useRef(false);

  const apiType = tab === "companies" ? "company" : "person";

  // Read URL -> state and do initial search
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const parsed = parseFromUrl(searchParams);
    const { tab: _tab, ...rest } = parsed;

    // Only set filters if there are actual search params besides defaults
    const hasAny =
      (rest.q && rest.q.trim()) ||
      (rest.country && rest.country.trim()) ||
      Object.entries(rest).some(([k, v]) => k !== "page" && k !== "pageSize" && (Array.isArray(v) ? v.length > 0 : v !== "" && v !== undefined));

    if (hasAny) {
      setFilters(rest);
      void executeSearch(rest, { updateUrl: false }); // URL already reflects current state
    }
  }, [searchParams]);

  const effectiveFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([k, v]) => {
      if (k === "page" || k === "pageSize") return false;
      if (k === "type") return false;
      if (!v) return false;
      return Array.isArray(v) ? v.length > 0 : true;
    }).length;
  }, [filters]);

  const executeSearch = useCallback(
    async (incoming = {}, { updateUrl = true } = {}) => {
      const normalized = normalizeIncomingFilters(incoming);

      // Merge with current filters, but allow incoming to overwrite
      const merged = {
        ...filters,
        ...normalized,
        type: apiType,
        page: toInt(normalized.page ?? filters.page, 1),
        pageSize: toInt(normalized.pageSize ?? filters.pageSize, 20),
      };

      // IMPORTANT: when switching tab/type, clear page back to 1 unless caller explicitly sets it
      if (normalized.page === undefined && normalized.pageSize === undefined && incoming !== filters) {
        merged.page = toInt(filters.page, 1);
        merged.pageSize = toInt(filters.pageSize, 20);
      }

      setLoading(true);
      try {
        const result = await searchApi(merged);
        setData(result);

        // Update local filters state after success (keeps UI in sync)
        setFilters((prev) => ({ ...prev, ...merged }));

        // Update URL
        if (updateUrl) {
          const url = buildUrlParams(tab, merged);
          setSearchParams(url, { replace: true });
        }
      } catch (e) {
        console.error(e);
        setData({ results: [], total: 0, page: 1, pageSize: 20 });
      } finally {
        setLoading(false);
      }
    },
    [apiType, filters, setSearchParams, tab],
  );

  const handleSearch = useCallback(
    (params) => {
      // SearchBar already passes { q, country, type, page: 1 }
      const next = normalizeIncomingFilters({ ...filters, ...params, type: apiType, page: 1 });
      void executeSearch(next);
    },
    [apiType, executeSearch, filters],
  );

  const handleQuickFilter = useCallback(
    (quick) => {
      // Quick filters should always target companies in the UI
      const nextTab = "companies";
      const normalized = normalizeIncomingFilters(quick);

      // Merge and reset page
      const next = { ...filters, ...normalized, page: 1 };

      // If user is on persons tab, jump to companies
      if (tab !== nextTab) {
        const url = new URLSearchParams();
        url.set("tab", nextTab);
        url.set("page", "1");
        url.set("pageSize", String(toInt(filters.pageSize, 20)));
        setSearchParams(url, { replace: true });
      }

      setFilters(next);
      void executeSearch(next);
    },
    [executeSearch, filters, setSearchParams, tab],
  );

  const handleAdvancedApply = useCallback(
    (advancedFilters) => {
      const normalized = normalizeIncomingFilters(advancedFilters);
      const next = { ...normalized, page: 1 };
      setFilters(next);
      void executeSearch(next);
    },
    [executeSearch],
  );

  const handleRemoveFilter = useCallback(
    (key, value) => {
      setFilters((prev) => {
        const next = { ...prev };

        // arrays
        if (Array.isArray(next[key])) {
          const filtered = next[key].filter((v) => v !== value);
          if (filtered.length) next[key] = filtered;
          else delete next[key];
        } else {
          delete next[key];
        }

        // always reset page when changing filters
        next.page = 1;

        void executeSearch(next);
        return next;
      });
    },
    [executeSearch],
  );

  const handleClearAll = useCallback(() => {
    setFilters({});
    setData(null);

    const url = new URLSearchParams();
    url.set("tab", tab);
    url.set("page", "1");
    url.set("pageSize", "20");
    setSearchParams(url, { replace: true });
  }, [setSearchParams, tab]);

  const switchTab = useCallback(
    (newTab) => {
      const url = new URLSearchParams();
      url.set("tab", newTab);
      url.set("page", "1");
      url.set("pageSize", String(toInt(filters.pageSize, 20)));
      setSearchParams(url, { replace: true });

      setFilters({});
      setData(null);
    },
    [filters.pageSize, setSearchParams],
  );

  // File-based imports (matches existing api.js)
  const handleDatabaseCustomersImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      await importDatabaseCustomers(file);
      // refresh results with current filters (if any)
      if (Object.keys(filters).length) await executeSearch({ ...filters, page: 1 });
    } catch (e) {
      console.error(e);
      notify("Import failed: " + (e?.message || "Unknown error"));
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  const handleFinalUpdatedEnrichment = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setEnriching(true);
    try {
      await importFinalUpdated(file);
      if (Object.keys(filters).length) await executeSearch({ ...filters, page: 1 });
    } catch (e) {
      console.error(e);
      notify("Enrich failed: " + (e?.message || "Unknown error"));
    } finally {
      setEnriching(false);
      event.target.value = "";
    }
  };

  // Pagination helpers
  const page = data?.page || toInt(filters.page, 1) || 1;
  const pageSize = data?.pageSize || toInt(filters.pageSize, 20) || 20;
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="container" style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 16px" }}>
      <h1 className="page-title">CBCS Compliance Search</h1>
      <p className="page-sub">Advanced search and compliance monitoring for trust company operations</p>

      <QuickFilterChips onFilterApply={handleQuickFilter} />

      {/* Tabs + Tools */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            type="button"
            onClick={() => switchTab("companies")}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "1px solid var(--border-primary)",
              background: tab === "companies" ? "var(--accent-primary)" : "var(--surface)",
              color: tab === "companies" ? "#fff" : "var(--text-secondary)",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
            COMPANIES
          </button>

          <button
            type="button"
            onClick={() => switchTab("persons")}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: "1px solid var(--border-primary)",
              background: tab === "persons" ? "var(--accent-primary)" : "var(--surface)",
              color: tab === "persons" ? "#fff" : "var(--text-secondary)",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
            PERSONS
          </button>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <label
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--border-primary)",
              background: "var(--surface)",
              color: "var(--text-secondary)",
              fontSize: 13,
              fontWeight: 600,
              cursor: importing ? "not-allowed" : "pointer",
              opacity: importing ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}>
            📥 {importing ? "Importing..." : "Import: DB Customers"}
            <input type="file" accept=".xlsx,.xls" onChange={handleDatabaseCustomersImport} disabled={importing} style={{ display: "none" }} />
          </label>

          <label
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--border-primary)",
              background: "var(--surface)",
              color: "var(--text-secondary)",
              fontSize: 13,
              fontWeight: 600,
              cursor: enriching ? "not-allowed" : "pointer",
              opacity: enriching ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}>
            📊 {enriching ? "Enriching..." : "Enrich: Final Updated"}
            <input type="file" accept=".xlsx,.xls" onChange={handleFinalUpdatedEnrichment} disabled={enriching} style={{ display: "none" }} />
          </label>

          <Link
            to="/overview"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid var(--border-primary)",
              background: "var(--accent-primary)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}>
            📊 Database Overview
          </Link>
        </div>
      </div>

      {/* Search bar (free-text + country + open modal) */}
      <SearchBar onSearch={handleSearch} loading={loading} defaultType={apiType} onOpenAdvanced={() => setShowAdvanced(true)} filterCount={effectiveFilterCount} />

      {/* Active filter chips */}
      <ActiveFiltersDisplay filters={filters} onRemove={handleRemoveFilter} onClearAll={handleClearAll} />

      {/* Result count */}
      {data && (
        <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>
          {(data.total || 0).toLocaleString()} result{data.total === 1 ? "" : "s"} found
        </div>
      )}

      <ResultsTable data={data} loading={loading} />

      {/* Pagination (simple + robust) */}
      {data && totalPages > 1 && (
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            Page <b>{page}</b> of <b>{totalPages}</b>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              disabled={!canPrev || loading}
              onClick={() => executeSearch({ ...filters, page: page - 1 })}
              style={{
                height: 40,
                padding: "0 14px",
                borderRadius: 10,
                border: "1px solid var(--border-primary)",
                background: "var(--surface)",
                color: "var(--text-secondary)",
                fontWeight: 700,
                cursor: !canPrev || loading ? "not-allowed" : "pointer",
                opacity: !canPrev || loading ? 0.6 : 1,
              }}>
              ← Prev
            </button>

            <button
              type="button"
              disabled={!canNext || loading}
              onClick={() => executeSearch({ ...filters, page: page + 1 })}
              style={{
                height: 40,
                padding: "0 14px",
                borderRadius: 10,
                border: "1px solid var(--border-primary)",
                background: "var(--surface)",
                color: "var(--text-secondary)",
                fontWeight: 700,
                cursor: !canNext || loading ? "not-allowed" : "pointer",
                opacity: !canNext || loading ? 0.6 : 1,
              }}>
              Next →
            </button>
          </div>
        </div>
      )}

      <AdvancedFilterModal open={showAdvanced} onClose={() => setShowAdvanced(false)} filters={filters} onApply={handleAdvancedApply} />
    </div>
  );
}
